import { Component, EventEmitter, OnDestroy, Output, TrackByFunction } from '@angular/core'
import { BehaviorSubject, debounceTime, map, Subject, switchMap, takeUntil } from 'rxjs'
import { Store } from '@ngrx/store'
import { CaseTreeNode } from '../../../state/cases/cases.state'
import { selectCases, selectExpandedFolderIds } from '../../../state/cases/cases.selectors'
import { selectCaseId } from '../../../state/case/case.selectors'
import { NestedTreeControl } from '@angular/cdk/tree'
import { ArrayDataSource } from '@angular/cdk/collections'
import { casesActions } from '../../../state/cases/cases.actions'
import { caseActions } from '../../../state/case/case.actions'
import { CdkDragDrop, CdkDragEnter, CdkDragStart } from '@angular/cdk/drag-drop'
import { findNodeById } from '../../../state/cases/dataset-browser.utils'
import { CaseTreeNodeViewModel } from './case-management.model'
import { Dialog } from '@angular/cdk/dialog'
import { DeleteNodeConfirmationDialogComponent } from './delete-node-confirmation-dialog/delete-node-confirmation-dialog.component'
import { DeleteNodeConfirmationDialogModel } from './delete-node-confirmation-dialog/delete-node-confirmation-dialog.model'
import { AddNewFolderDialogComponent } from './add-new-folder-dialog/add-new-folder-dialog.component'
import { AddNewFolderDialogModel } from './add-new-folder-dialog/add-new-folder-dialog.model'
import { StoreUtilsService } from '../../../shared/services/store-utils.service'
import { environment } from 'src/environments/environment'

@Component({
    selector: 'app-case-management',
    templateUrl: './case-management.component.html',
    styleUrls: ['./case-management.component.scss'],
})
export class CaseManagementComponent implements OnDestroy {
    // todo: prevent datasource re-setting from changing expansion states and scroll position

    @Output()
    createNewDataset = new EventEmitter<void>()

    @Output()
    openUnsubmittedDataset = new EventEmitter<{ id: number; name: string }>()

    readonly currentCaseId$ = this.store.select(selectCaseId)

    readonly rootDropTargetId = 'drop-list-folder-id-root'
    private dropTargets: CaseTreeNodeViewModel[] = []
    private readonly wikiUrl = environment.wikiUrl

    private readonly _cases$: BehaviorSubject<CaseTreeNodeViewModel[]> = new BehaviorSubject([])
    readonly dataSource$: ArrayDataSource<CaseTreeNodeViewModel> = new ArrayDataSource(
        this._cases$.asObservable().pipe(
            // Currently assuming that all currently uploading datasets are at root level
            map(datasets => datasets.filter((node: CaseTreeNode) => node.currently_uploading !== 1))
        )
    )
    readonly treeControl = new NestedTreeControl<CaseTreeNodeViewModel>(node => node.children ?? [])

    readonly unsubmittedDatasets$ = this._cases$.pipe(
        map(datasets => datasets.filter(dataset => dataset.currently_uploading === 1))
    )

    readonly trackByFn: TrackByFunction<CaseTreeNodeViewModel> = (
        _: number,
        node: CaseTreeNodeViewModel
    ): number => {
        return node.id ?? -1
    }

    private readonly dropListEnter$: Subject<CaseTreeNodeViewModel | null> = new Subject()

    get dropTargetIds(): string[] {
        // drop target order determines if nested drop-lists work
        //  it seems the list is iterated and the first match will be the drop target
        //  so deeper nested lists have to be further up the list
        return [
            ...this.dropTargets.map(node => `drop-list-folder-id-${node.id}`),
            this.rootDropTargetId,
        ]
    }

    readonly isFolder = (_: number, node: CaseTreeNode): boolean => !!node.children

    private readonly prepareViewModel = (
        cases: CaseTreeNode[],
        nestingLevel = 0,
        parent: CaseTreeNode | null = null
    ): CaseTreeNodeViewModel[] => {
        const viewModels = cases.map(
            (node: CaseTreeNodeViewModel): CaseTreeNodeViewModel => ({
                ...node,
                nestingLevel: nestingLevel,
                parent: parent,
                children: node.children
                    ? this.prepareViewModel(node.children, nestingLevel + 1, node)
                    : node.children,
            })
        )
        this.dropTargets.push(...viewModels.filter(node => this.isFolder(0, node)))
        return [
            ...viewModels
                .filter(node => node.children !== null)
                .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)),
            ...viewModels
                .filter(node => node.children === null)
                .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)),
        ]
    }

    private readonly destroy$ = new Subject<void>()

    constructor(
        private readonly store: Store,
        private readonly storeUtils: StoreUtilsService,
        private readonly dialog: Dialog
    ) {
        this.setUpCasesSubscription()
        this.setUpRestoreTreeViewStateSubscription()
        this.setUpStoreTreeViewStateSubscription()
        this.setUpExpandFolderOnHoverSubscription()
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    newDatasetClicked(): void {
        this.createNewDataset.emit()
    }

    goToUnsubmittedDataset(node: CaseTreeNodeViewModel): void {
        this.openUnsubmittedDataset.emit({ id: node.id, name: node.name })
    }

    setCase(chosenCase: CaseTreeNode): void {
        this.store.dispatch(caseActions.selectCase({ caseId: chosenCase.id }))
    }

    getNodeIconShape(node: CaseTreeNodeViewModel): string {
        return node.is_ready ? 'file' : 'cog'
    }

    deleteFolder(node: CaseTreeNode): void {
        const data: DeleteNodeConfirmationDialogModel = { type: 'folder', name: node.name }
        const dialogRef = this.dialog.open<boolean>(DeleteNodeConfirmationDialogComponent, {
            data,
            autoFocus: 'dialog',
        })
        dialogRef.closed.pipe(takeUntil(this.destroy$)).subscribe(confirmed => {
            if (!confirmed) return
            this.store.dispatch(casesActions.deleteFolder({ folderId: node.id }))
        })
    }

    createFolder(node: CaseTreeNode | null): void {
        const data: AddNewFolderDialogModel = { name: node?.name ?? '/' }
        const dialogRef = this.dialog.open<string>(AddNewFolderDialogComponent, {
            data,
            autoFocus: 'first-tabbable',
        })
        dialogRef.closed.pipe(takeUntil(this.destroy$)).subscribe(folderName => {
            if (!folderName) return
            this.store.dispatch(
                casesActions.createFolder({ name: folderName, parentId: node?.id ?? null })
            )
        })
    }

    deleteFile(node: CaseTreeNodeViewModel): void {
        const data: DeleteNodeConfirmationDialogModel = { type: 'dataset', name: node.name }
        const dialogRef = this.dialog.open<boolean>(DeleteNodeConfirmationDialogComponent, {
            data,
            autoFocus: 'dialog',
        })
        dialogRef.closed.pipe(takeUntil(this.destroy$)).subscribe(confirmed => {
            if (!confirmed) return
            this.store.dispatch(casesActions.deleteDataset({ datasetId: node.id }))
        })
    }

    onDropNode(
        e: CdkDragDrop<CaseTreeNodeViewModel, CaseTreeNodeViewModel, CaseTreeNodeViewModel>
    ): void {
        if (e.previousContainer === e.container) return

        const node = e.item.data
        const target = e.container.data
        this.moveNode(node, target?.id ?? null)
    }

    onDropNodeToRoot(
        e: CdkDragDrop<CaseTreeNodeViewModel, CaseTreeNodeViewModel, CaseTreeNodeViewModel>
    ): void {
        const node = e.item.data
        if (node.parent_id === null) return

        this.moveNode(node, null)
    }

    private moveNode(node: CaseTreeNodeViewModel, target: number | null): void {
        if (node.children) {
            this.store.dispatch(casesActions.moveFolder({ folderId: node.id, parentId: target }))
        } else {
            this.store.dispatch(casesActions.moveDataset({ datasetId: node.id, parentId: target }))
        }
    }

    onDragStart(e: CdkDragStart<CaseTreeNodeViewModel>): void {
        this.sortDropTargets()
        this.treeControl.toggleDescendants(e.source.data)
    }

    onDropListEntered(e: CdkDragEnter<CaseTreeNodeViewModel> | null): void {
        this.dropListEnter$.next(e?.container.data ?? null)
    }

    private sortDropTargets(): void {
        this.dropTargets = this.dropTargets.sort((a, b) =>
            b.nestingLevel > a.nestingLevel ? 1 : -1
        )
    }

    private isExpandableNode(node: CaseTreeNodeViewModel | null | undefined): boolean {
        return (
            !!node &&
            !!node.children &&
            node.children.length > 0 &&
            !this.treeControl.isExpanded(node)
        )
    }

    private setUpCasesSubscription(): void {
        this.store
            .select(selectCases)
            .pipe(takeUntil(this.destroy$))
            .subscribe((cases: CaseTreeNode[]): void => {
                const viewModel = this.prepareViewModel(cases)
                this._cases$.next([])
                // todo: fix timing hack with setTimeout
                setTimeout(() => {
                    this._cases$.next(viewModel)
                })
            })
    }

    private setUpRestoreTreeViewStateSubscription(): void {
        this._cases$
            .pipe(
                takeUntil(this.destroy$),
                switchMap(cases =>
                    this.storeUtils
                        .snapshotObservable(selectExpandedFolderIds)
                        .pipe(map(folderIds => [cases, folderIds]))
                )
            )
            .subscribe(([cases, folderIds]: [CaseTreeNodeViewModel[], number[]]): void => {
                folderIds.forEach(id => {
                    const node = findNodeById(id, cases)
                    if (this.isExpandableNode(node)) this.treeControl.expand(node!)
                })
            })
    }

    private setUpExpandFolderOnHoverSubscription(): void {
        this.dropListEnter$.pipe(takeUntil(this.destroy$), debounceTime(500)).subscribe(node => {
            if (this.isExpandableNode(node)) this.treeControl.expand(node!)
        })
    }

    private setUpStoreTreeViewStateSubscription(): void {
        this.treeControl.expansionModel.changed.pipe(takeUntil(this.destroy$)).subscribe(event => {
            this.sortDropTargets()
            this.store.dispatch(
                casesActions.setExpandedFolders({
                    folderIds: event.source.selected.map(c => c.id),
                })
            )
        })
    }

    goToWiki(): void {
        window.open(
            `${this.wikiUrl}home/Data_management/Upload#dataset-management-window`,
            '_blank'
        )
    }
}
